import i18n from "i18next";

export type QuoteApprovalPayload = {
  quoteId: string;
  quoteNumber: string;
  clientName: string;
  totalPrice: string;
  quoteUrl: string;
};

export type QuoteApprovalMessage = {
  content: string;
  conversationName: string;
  metadata: {
    type: "quote_approval";
    quoteApproval: QuoteApprovalPayload;
  };
};

const SUPPORTED_LANGS = new Set(["en", "he", "ar", "de"]);

export function resolveManagerLanguage(employeeData: unknown): string {
  const data = (employeeData || {}) as Record<string, unknown>;
  const employee =
    (data.employee as Record<string, unknown> | undefined) ||
    (data.Employee as Record<string, unknown> | undefined) ||
    {};
  const raw =
    data.systemLanguage ||
    data.systemLang ||
    employee.systemLanguage ||
    employee.systemLang ||
    "en";
  const normalized = String(raw).toLowerCase().split(/[-_]/)[0] || "en";
  return SUPPORTED_LANGS.has(normalized) ? normalized : "en";
}

export function formatQuoteTotalPrice(
  quote: {
    totalPrice?: number | string | null;
    totalAmount?: number | string | null;
    totalPayment?: number | string | null;
    currency?: number | string | null;
  },
  currencyUnits?: Array<{ value?: number | string; text?: string }> | null,
): string {
  const raw =
    quote?.totalPrice ?? quote?.totalPayment ?? quote?.totalAmount ?? null;
  if (raw == null || raw === "") return "N/A";

  const amount = Number(raw);
  const formatted = Number.isFinite(amount)
    ? amount.toLocaleString(undefined, {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      })
    : String(raw);

  const unit =
    currencyUnits?.find((c) => String(c.value) === String(quote?.currency))
      ?.text || "";
  return unit ? `${unit} ${formatted}` : formatted;
}

export function buildQuoteUrl(quoteId: string): string {
  if (typeof window === "undefined" || !quoteId) return `/quote?Id=${quoteId}`;
  return `${window.location.origin}/quote?Id=${encodeURIComponent(quoteId)}`;
}

/** Detect quote-approval conversation titles stored in any language. */
export function extractQuoteApprovalNumberFromTitle(
  name?: string | null,
): string | null {
  const raw = String(name || "").trim();
  if (!raw) return null;

  const knownPrefix = raw.match(
    /^(?:Quote Approval|בקשה לאישור הצעת מחיר|אישור הצעת מחיר|طلب الموافقة على عرض السعر|Angebotsfreigabe)\s*[-–—:]\s*(.+)$/i,
  );
  if (knownPrefix?.[1]?.trim()) return knownPrefix[1].trim();

  if (/quote\s*approval/i.test(raw)) {
    const parts = raw.split(/[-–—:]/);
    const last = parts[parts.length - 1]?.trim();
    if (last) return last;
  }

  return null;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Re-localize a stored quote-approval conversation title for the current UI language. */
export function localizeQuoteApprovalConversationTitle(
  name: string | undefined | null,
  t: TranslateFn,
  contextType?: string | null,
): string | null {
  const fromTitle = extractQuoteApprovalNumberFromTitle(name);
  const fromContext =
    !fromTitle && contextType === "QUOTE"
      ? String(name || "")
          .split(/[-–—:]/)
          .pop()
          ?.trim() || null
      : null;
  const number = fromTitle || fromContext;
  if (!number) return null;

  return t("sales.quote.quoteApprovalConversationName", {
    number,
    defaultValue: `Quote Approval - ${number}`,
  });
}

export function buildQuoteApprovalMessage(params: {
  managerLang: string;
  quoteId: string;
  quoteNumber: string;
  clientName: string;
  totalPrice: string;
  quoteUrl: string;
}): QuoteApprovalMessage {
  const t = i18n.getFixedT(params.managerLang);
  const quoteNumber = params.quoteNumber || "N/A";
  const clientName = params.clientName || "N/A";
  const totalPrice = params.totalPrice || "N/A";

  const title = t("sales.quote.quoteApprovalRequestTitle", {
    defaultValue: "Quote Approval Request",
  });
  const quoteNumberLabel = t("sales.quote.quoteApprovalQuoteNumber", {
    defaultValue: "Quote Number",
  });
  const clientLabel = t("sales.quote.quoteApprovalClientName", {
    defaultValue: "Client Name",
  });
  const totalLabel = t("sales.quote.quoteApprovalTotalPrice", {
    defaultValue: "Total Price",
  });
  const pleaseReview = t("sales.quote.quoteApprovalPleaseReview", {
    defaultValue: "Please review and approve this quote.",
  });
  const conversationName = t("sales.quote.quoteApprovalConversationName", {
    number: quoteNumber,
    defaultValue: `Quote Approval - ${quoteNumber}`,
  });

  // Keep useful fields at the end — list previews truncate from the start.
  // Do not put quote URL/GUID in content; the Open quote button uses metadata.
  const content = [
    `📄 ${title}`,
    "",
    pleaseReview,
    "",
    `${quoteNumberLabel}: ${quoteNumber}`,
    `${clientLabel}: ${clientName}`,
    `${totalLabel}: ${totalPrice}`,
  ].join("\n");

  return {
    content,
    conversationName,
    metadata: {
      type: "quote_approval",
      quoteApproval: {
        quoteId: params.quoteId,
        quoteNumber,
        clientName,
        totalPrice,
        quoteUrl: params.quoteUrl,
      },
    },
  };
}

export function buildQuoteApprovalResultMessage(params: {
  outcome: "approved" | "rejected";
  quoteNumber?: string;
  lang?: string;
}): { content: string; metadata: Record<string, unknown> } {
  const lang = params.lang || i18n.language || "en";
  const t = i18n.getFixedT(lang);
  const number = params.quoteNumber || "N/A";
  const content =
    params.outcome === "approved"
      ? t("sales.quote.quoteApprovalManagerApproved", {
          number,
          defaultValue: `Quote ${number} was approved by the manager.`,
        })
      : t("sales.quote.quoteApprovalManagerRejected", {
          number,
          defaultValue: `Quote ${number} was rejected by the manager.`,
        });

  return {
    content,
    metadata: {
      type: "quote_approval_result",
      outcome: params.outcome,
      quoteNumber: number,
    },
  };
}

/** Detect stored approve/reject result text written in any supported language. */
export function extractQuoteApprovalResultFromContent(
  content?: string | null,
): { outcome: "approved" | "rejected"; quoteNumber: string } | null {
  const text = String(content || "").trim();
  if (!text) return null;

  const en = text.match(
    /^Quote\s+(.+?)\s+was\s+(approved|rejected)\s+by\s+the\s+manager\.?$/i,
  );
  if (en?.[1] && en[2]) {
    return {
      outcome: en[2].toLowerCase() === "approved" ? "approved" : "rejected",
      quoteNumber: en[1].trim(),
    };
  }

  const he = text.match(
    /^הצעת מחיר\s+(.+?)\s+(אושרה|נדחתה)\s+על ידי המנהל\.?$/,
  );
  if (he?.[1] && he[2]) {
    return {
      outcome: he[2] === "אושרה" ? "approved" : "rejected",
      quoteNumber: he[1].trim(),
    };
  }

  const ar = text.match(
    /^تم\s+(الموافقة على|رفض)\s+عرض السعر\s+(.+?)\s+من قبل المدير\.?$/,
  );
  if (ar?.[1] && ar[2]) {
    return {
      outcome: ar[1].includes("رفض") ? "rejected" : "approved",
      quoteNumber: ar[2].trim(),
    };
  }

  const de = text.match(
    /^Angebot\s+(.+?)\s+wurde\s+vom\s+Manager\s+(genehmigt|abgelehnt)\.?$/i,
  );
  if (de?.[1] && de[2]) {
    return {
      outcome: de[2].toLowerCase() === "genehmigt" ? "approved" : "rejected",
      quoteNumber: de[1].trim(),
    };
  }

  return null;
}

/** Re-localize a stored approve/reject preview for the current UI language. */
export function localizeQuoteApprovalResultPreview(
  content: string | undefined | null,
  t?: TranslateFn,
): string | null {
  const parsed = extractQuoteApprovalResultFromContent(content);
  if (!parsed) return null;

  const translate = t || ((key, options) => i18n.t(key, options));
  const number = parsed.quoteNumber || "N/A";
  return parsed.outcome === "approved"
    ? translate("sales.quote.quoteApprovalManagerApproved", {
        number,
        defaultValue: `Quote ${number} was approved by the manager.`,
      })
    : translate("sales.quote.quoteApprovalManagerRejected", {
        number,
        defaultValue: `Quote ${number} was rejected by the manager.`,
      });
}

const QUOTE_NUMBER_LABELS = [
  "Quote Number",
  "מ. הצעת מחיר",
  "מספר הצעה",
  "رقم عرض السعر",
  "Angebotsnummer",
];
const CLIENT_LABELS = [
  "Client Name",
  "Client",
  "שם הלקוח",
  "שם לקוח",
  "اسم العميل",
  "Kundenname",
];
const TOTAL_LABELS = [
  "Total Price",
  "Total Amount",
  "מחיר כולל",
  "السعر الإجمالي",
  "Gesamtpreis",
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractLabeledField(text: string, labels: string[]): string | undefined {
  const allLabels = [...QUOTE_NUMBER_LABELS, ...CLIENT_LABELS, ...TOTAL_LABELS];
  for (const label of labels) {
    const match = text.match(
      new RegExp(`${escapeRegExp(label)}\\s*:\\s*([^\\n]+)`, "i"),
    );
    if (!match?.[1]) continue;
    let value = match[1].trim();
    // Truncated / single-line notification bodies may include the next label.
    for (const other of allLabels) {
      if (other.toLowerCase() === label.toLowerCase()) continue;
      const cut = value.search(
        new RegExp(`\\s+${escapeRegExp(other)}\\s*:`, "i"),
      );
      if (cut > 0) {
        value = value.slice(0, cut).trim();
        break;
      }
    }
    if (value) return value;
  }
  return undefined;
}

function isQuoteApprovalRequestContent(text: string): boolean {
  if (
    /Quote Approval Request|בקשה לאישור הצעת מחיר|طلب الموافقة على عرض السعر|Angebotsfreigabe/i.test(
      text,
    )
  ) {
    return true;
  }
  const hasNumber = QUOTE_NUMBER_LABELS.some((l) =>
    new RegExp(`${escapeRegExp(l)}\\s*:`, "i").test(text),
  );
  const hasReviewOrFields =
    /Please review|אנא עיין|Please review and approve/i.test(text) ||
    CLIENT_LABELS.some((l) =>
      new RegExp(`${escapeRegExp(l)}\\s*:`, "i").test(text),
    ) ||
    TOTAL_LABELS.some((l) =>
      new RegExp(`${escapeRegExp(l)}\\s*:`, "i").test(text),
    );
  return hasNumber && hasReviewOrFields;
}

/** Rebuild quote-approval request preview in the viewer language. */
export function localizeQuoteApprovalRequestPreview(
  content: string | undefined | null,
  t?: TranslateFn,
): string | null {
  const raw = String(content || "").trim();
  if (!raw || !isQuoteApprovalRequestContent(raw)) return null;

  const translate = t || ((key, options) => i18n.t(key, options));
  const quoteNumber = extractLabeledField(raw, QUOTE_NUMBER_LABELS);
  const clientName = extractLabeledField(raw, CLIENT_LABELS);
  const totalPrice = extractLabeledField(raw, TOTAL_LABELS);

  const title = translate("sales.quote.quoteApprovalRequestTitle", {
    defaultValue: "Quote Approval Request",
  });

  const parts: string[] = [`📄 ${title}`];
  if (quoteNumber) {
    parts.push(
      `${translate("sales.quote.quoteApprovalQuoteNumber", {
        defaultValue: "Quote Number",
      })}: ${quoteNumber}`,
    );
  }
  if (clientName) {
    parts.push(
      `${translate("sales.quote.quoteApprovalClientName", {
        defaultValue: "Client Name",
      })}: ${clientName}`,
    );
  }
  if (totalPrice) {
    parts.push(
      `${translate("sales.quote.quoteApprovalTotalPrice", {
        defaultValue: "Total Price",
      })}: ${totalPrice}`,
    );
  }

  if (parts.length === 1) {
    return `${title} — ${translate("sales.quote.quoteApprovalPleaseReview", {
      defaultValue: "Please review and approve this quote.",
    })}`;
  }

  return parts.join(" · ");
}

/** Clean list/preview text so quote URLs and GUIDs are never shown. */
export function formatInternalChatPreview(
  content?: string | null,
  t?: TranslateFn,
): string {
  if (!content) return "";

  const localizedResult = localizeQuoteApprovalResultPreview(content, t);
  if (localizedResult) return localizedResult;

  const localizedRequest = localizeQuoteApprovalRequestPreview(content, t);
  if (localizedRequest) return localizedRequest;

  let text = String(content)
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/\/quote\?Id=[^\s)]+/gi, "")
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
      "",
    )
    .replace(/^\s*(Open quote|פתח הצעת מחיר|فتح عرض السعر|Angebot öffnen)\s*:?\s*/gim, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

  return text;
}
